import { reactive, watch } from "vue";
import {
  type Order,
  type Event,
  type OrderEvent,
  type ShopEvent,
  takePollingManager,
  orderLoadApi,
  loadExecutorsApi,
  ResponseCode,
  orderListWidthCursorApi,
  orderHistoryWidthCursorApi,
  shopLoadApi,
} from "@src/api";
import {
  useOrderListStore,
  useExecutorListStore,
  useSettingsStore,
  useOrderListFiltersStore,
  useUserInfoStore,
} from "@src/store";

const state = reactive<{ events: Event[] }>({
  events: [],
});

const beepAudio = new Audio("/notification.ogg");

export const useEventsPolling = () => {
  const orderListStore = useOrderListStore();
  const executorListStore = useExecutorListStore();
  const settingsStore = useSettingsStore();
  const orderListFiltersStore = useOrderListFiltersStore();
  const user_info_store = useUserInfoStore();

  // TODO: надо придумать ещё один слой в котором будет производится работа с api, какие-то вычисления
  const getExecutorsIds = (orders: Order[]) => {
    const executorsIds = new Set<string>();

    orders.forEach((order) => {
      if (order.executed_by) executorsIds.add(order.executed_by);
    });

    return Array.from(executorsIds);
  };
  // обработчики событий по их ключам
  const event_handlers_map: Record<string, { handler: (event: unknown) => Promise<void> }> = {
    [`order:shop:${user_info_store.shop?.shop_id || ""}`]: {
      handler: async (event: OrderEvent) => {
        // если пришёл признак archived, то заказ более не отображаем
        if (event.data.archived) {
          orderListStore.deleteOrder(event.data.order_id);

          return;
        }

        const result = await orderLoadApi({ order_id: [event.data.order_id] });

        if (result.code !== ResponseCode.OK) return;

        const order = result.orders[0];
        const order_store = orderListStore.get(order.order_id);

        if (!order_store) {
          orderListStore.add(order);

          if (
            settingsStore.location === "store" &&
            (settingsStore.store_types.length === 0 || settingsStore.storeTypeIsSelected(order.place_from_type)) &&
            (!orderListFiltersStore.section_exist || orderListFiltersStore.sectionIsSelected(order.place_section))
          )
            beepAudio.play();
        } else if (order_store.revision < order.revision) {
          orderListStore.updateOrder(order);

          if (order.executed_by && !executorListStore.has(order.executed_by)) {
            const resultExecutorLoad = await loadExecutorsApi({ user_id: [order.executed_by] });

            if (resultExecutorLoad.code !== ResponseCode.OK) return;

            executorListStore.add(resultExecutorLoad.executors);
          }
        }
      },
    },
    [`shop:${user_info_store.shop?.shop_id || ""}`]: {
      handler: async (event: ShopEvent) => {
        if (event.data.type === "shop_update") {
          const result = await shopLoadApi({ shop_id: event.data.shop_id });

          if (result.code !== ResponseCode.OK) return;

          user_info_store.setShop(result.shop);

          const shop_store_types = Object.entries(result.shop.store_types)
            .filter(([, value]) => value)
            .map(([key]) => key);
          const store_types = settingsStore.store_types.filter((item) => shop_store_types.includes(item));

          settingsStore.setStoreTypes(store_types);
        }
      },
    },
  };

  const startPolling = (params: { keys: string[][] }) => {
    if (takePollingManager.isRunning) return;

    params.keys.forEach((item) => {
      takePollingManager.addKeys(item);
    });

    takePollingManager.setResponseCallback(async (response) => {
      if (response.code === ResponseCode.MAYBE_LOST) {
        takePollingManager.stop();
        orderListStore.clear();

        takePollingManager.start();

        const resultOrderList = await orderListWidthCursorApi();
        const resultOrderHistory = await orderHistoryWidthCursorApi();
        const executorsIds = getExecutorsIds([...resultOrderList, ...resultOrderHistory]);
        const resultExecutorList = await loadExecutorsApi({ user_id: executorsIds });

        executorListStore.add(resultExecutorList.executors);
        orderListStore.set(resultOrderList);
        orderListStore.setHistory(resultOrderHistory);
      }

      state.events.push(...response.events);
    });

    takePollingManager.start();
  };

  const stopPolling = () => {
    takePollingManager.stop();
  };

  watch(state.events, async (newEvents) => {
    while (newEvents.length > 0) {
      const event = newEvents.shift();
      const event_handler = event_handlers_map[event.key];

      if (event_handler) {
        await event_handler.handler(event);
      }
    }
  });

  return {
    startPolling,
    stopPolling,
  };
};
